import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

function formatDateTime(d) {
  try {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString();
  } catch {
    return '';
  }
}

function toPublicUrl(baseUrl, storedUrl) {
  const p = String(storedUrl || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(baseUrl || '').replace(/\/$/, '');
  const rel = p.startsWith('/') ? p : `/${p}`;
  return base + rel;
}

function getFilenameFromContentDisposition(headerValue) {
  const h = String(headerValue || '');
  const m = h.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const raw = (m && (m[1] || m[2])) ? (m[1] || m[2]) : '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export default function APNClipsModerationAdmin() {
  const { user } = useContext(UserContext);
  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);

  const [filters, setFilters] = useState({
    status: 'all',
    moderationStatus: 'all',
    mode: 'all',
    year: '',
    month: '',
    category: '',
    platform: '',
    creator: '',
    userId: '',
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const openItem = useMemo(() => items.find((x) => x.id === openId) || null, [items, openId]);
  const openVideoUrl = useMemo(() => (openItem ? toPublicUrl(baseUrl, openItem.storedUrl) : ''), [openItem, baseUrl]);

  const load = async (next = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (next.status && next.status !== 'all') params.status = next.status;
      if (next.moderationStatus && next.moderationStatus !== 'all') params.moderationStatus = next.moderationStatus;
      if (next.mode && next.mode !== 'all') params.mode = next.mode;
      if (String(next.year || '').trim()) params.year = String(next.year).trim();
      if (String(next.month || '').trim()) params.month = String(next.month).trim();
      if (String(next.category || '').trim()) params.category = String(next.category).trim();
      if (String(next.platform || '').trim()) params.platform = String(next.platform).trim();
      if (String(next.creator || '').trim()) params.creator = String(next.creator).trim();
      if (String(next.userId || '').trim()) params.userId = String(next.userId).trim();

      const res = await api.get('/apn-clips/admin/submissions', { params });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte Admin-Liste nicht laden.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = async () => {
    const empty = {
      status: 'all',
      moderationStatus: 'all',
      mode: 'all',
      year: '',
      month: '',
      category: '',
      platform: '',
      creator: '',
      userId: '',
    };
    setFilters(empty);
    await load(empty);
  };

  const downloadClip = async (id) => {
    if (!id) return;
    setBusyId(id);
    setError('');
    try {
      const res = await api.get(`/apn-clips/admin/submissions/${id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = getFilenameFromContentDisposition(res.headers?.['content-disposition']) || `clip-${id}.mp4`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || 'Download fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const downloadMetadata = async (id) => {
    if (!id) return;
    setBusyId(id);
    setError('');
    try {
      const res = await api.get(`/apn-clips/admin/submissions/${id}/metadata`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = getFilenameFromContentDisposition(res.headers?.['content-disposition']) || `clip-${id}.txt`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || 'Metadaten-Download fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const deleteClip = async (id) => {
    if (!id) return;
    const ok = window.confirm('Diesen Clip wirklich endgültig löschen? (Dieser Vorgang kann nicht rückgängig gemacht werden.)');
    if (!ok) return;

    setBusyId(id);
    setError('');
    try {
      await api.delete(`/apn-clips/admin/submissions/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (openId === id) setOpenId(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'Löschen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const getUserLabel = (sub) => {
    const dn = String(sub?.user?.displayName || '').trim();
    const un = String(sub?.user?.username || '').trim();
    return dn || un || `User #${sub?.userId || '?'}`;
  };

  const getModeratedByLabel = (sub) => {
    const dn = String(sub?.moderatedBy?.displayName || '').trim();
    const un = String(sub?.moderatedBy?.username || '').trim();
    return dn || un || (sub?.moderatedByUserId ? `User #${sub.moderatedByUserId}` : '—');
  };

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Moderation (Admin)</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Alle Clips (unabhängig von Datum/Status) durchsuchen, herunterladen oder endgültig löschen.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips/moderation" style={{ textDecoration: 'none' }}>Zurück</Link>
          <button className="hero-btn-primary" type="button" onClick={() => load()} disabled={loading}>Neu laden</button>
          <button className="hero-btn-secondary" type="button" onClick={reset} disabled={loading}>Reset</button>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Filter</h2>

          {error && (
            <div style={{ padding: '0 8px 12px', color: '#ffdddd', fontSize: 13 }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, padding: '0 8px 12px' }}>
            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Status
              <select
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(20,20,20,0.8)', color: '#fff' }}
              >
                <option value="all">(alle)</option>
                <option value="processing">processing</option>
                <option value="ready">ready</option>
                <option value="failed">failed</option>
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Moderation
              <select
                value={filters.moderationStatus}
                onChange={(e) => setFilters((p) => ({ ...p, moderationStatus: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(20,20,20,0.8)', color: '#fff' }}
              >
                <option value="all">(alle)</option>
                <option value="pending">pending</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Modus
              <select
                value={filters.mode}
                onChange={(e) => setFilters((p) => ({ ...p, mode: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(20,20,20,0.8)', color: '#fff' }}
              >
                <option value="all">(alle)</option>
                <option value="upload">upload</option>
                <option value="link">link</option>
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Jahr
              <input
                value={filters.year}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((p) => ({ ...p, year: v, month: String(v || '').trim() ? p.month : '' }));
                }}
                placeholder="z.B. 2026"
                inputMode="numeric"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Monat
              <select
                value={filters.month}
                onChange={(e) => setFilters((p) => ({ ...p, month: e.target.value }))}
                disabled={!String(filters.year || '').trim()}
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(20,20,20,0.8)', color: '#fff' }}
              >
                <option value="">(alle)</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={String(m)}>{String(m).padStart(2, '0')}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Kategorie
              <input
                value={filters.category}
                onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                placeholder="z.B. Shooter"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Plattform
              <input
                value={filters.platform}
                onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
                placeholder="z.B. Twitch"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Creator
              <input
                value={filters.creator}
                onChange={(e) => setFilters((p) => ({ ...p, creator: e.target.value }))}
                placeholder="Name"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              User-ID
              <input
                value={filters.userId}
                onChange={(e) => setFilters((p) => ({ ...p, userId: e.target.value }))}
                placeholder="z.B. 123"
                inputMode="numeric"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', padding: '0 8px 8px' }}>
            <button className="hero-btn-primary" type="button" onClick={() => load()} disabled={loading}>Suchen</button>
            <button className="hero-btn-secondary" type="button" onClick={reset} disabled={loading}>Reset</button>
          </div>
        </div>

        <div className="highlight-card" style={{ ...card, alignItems: 'stretch', marginTop: 12 }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Clips</h2>

          {loading ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Lade…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Keine Treffer.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12, padding: '0 8px 8px' }}>
              {items.map((s) => {
                const id = s?.id;
                const isReady = String(s?.status) === 'ready';
                const videoUrl = isReady ? toPublicUrl(baseUrl, s?.storedUrl) : '';
                const thumb = String(s?.thumbnailUrl || '').trim();

                return (
                  <div key={id} className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 800, fontSize: 16 }}>
                        #{id} — {String(s?.title || '(ohne Titel)')}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        Status: <b>{String(s?.status || '—')}</b> &nbsp;|&nbsp; Moderation: <b>{String(s?.moderationStatus || '—')}</b>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                      <div>
                        <div><b>Creator:</b> {String(s?.creatorName || '—')}</div>
                        <div><b>Von User:</b> {getUserLabel(s)}</div>
                        <div><b>Plattform:</b> {String(s?.platform || '—')}</div>
                        <div><b>Kategorie:</b> {String(s?.category || '—')}</div>
                        <div><b>Modus:</b> {String(s?.mode || '—')}</div>
                      </div>
                      <div>
                        <div><b>Rechte bestätigt:</b> {s?.rightsConfirmed ? 'Ja' : 'Nein'}</div>
                        <div><b>Erstellt:</b> {formatDateTime(s?.createdAt) || '—'}</div>
                        <div><b>Moderiert von:</b> {getModeratedByLabel(s)}</div>
                        <div><b>Moderiert am:</b> {formatDateTime(s?.moderatedAt) || '—'}</div>
                      </div>
                    </div>

                    {String(s?.mode) === 'link' && s?.sourceUrl && (
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                        <b>Quelle:</b>{' '}
                        <a href={s.sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                          {String(s.sourceUrl)}
                        </a>
                      </div>
                    )}

                    {thumb && (
                      <button
                        type="button"
                        onClick={() => setOpenId(id)}
                        style={{
                          marginTop: 12,
                          padding: 0,
                          border: '1px solid rgba(255,255,255,0.10)',
                          borderRadius: 12,
                          overflow: 'hidden',
                          background: 'transparent',
                          cursor: 'pointer',
                        }}
                        title="Clip ansehen"
                      >
                        <img alt={String(s?.title || '')} src={thumb} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                      </button>
                    )}

                    {isReady && videoUrl && (
                      <div style={{ marginTop: 12 }}>
                        <video
                          controls
                          src={videoUrl}
                          style={{ width: '100%', maxWidth: 920, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <button
                        type="button"
                        className="hero-btn-secondary"
                        disabled={busyId === id}
                        onClick={() => downloadClip(id)}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        className="hero-btn-secondary"
                        disabled={busyId === id}
                        onClick={() => downloadMetadata(id)}
                      >
                        Metadaten herunterladen
                      </button>
                      <button
                        type="button"
                        className="hero-btn-secondary"
                        disabled={busyId === id}
                        onClick={() => deleteClip(id)}
                        title="Löscht auch Datei/Votes endgültig"
                      >
                        Endgültig löschen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {openItem && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="highlight-card"
            style={{
              ...card,
              width: '100%',
              maxWidth: 980,
              alignItems: 'stretch',
              display: 'grid',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                {String(openItem?.title || '(ohne Titel)')}
              </div>
              <button type="button" className="hero-btn-secondary" onClick={() => setOpenId(null)}>Schließen</button>
            </div>

            {openVideoUrl ? (
              <video
                controls
                src={openVideoUrl}
                style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
              />
            ) : (
              <div style={{ opacity: 0.85, fontSize: 13 }}>Video ist nicht verfügbar.</div>
            )}

            <div style={{ fontSize: 13, opacity: 0.9, display: 'grid', gap: 4 }}>
              <div><b>Creator:</b> {String(openItem?.creatorName || '—')}</div>
              <div><b>Plattform:</b> {String(openItem?.platform || '—')}</div>
              <div><b>Kategorie:</b> {String(openItem?.category || '—')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
